import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { useBasePath } from '@/lib/paths'
import { formatDate } from '@/lib/utils'
import { NomineeDirectorSummary } from '@/types'
import { Plus, Users } from 'lucide-react'

type Props = {
  nominee_directors: NomineeDirectorSummary[]
  search: string
  pagination: PaginationMeta
}

export default function NomineeDirectorsIndex({ nominee_directors, search, pagination }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout
      title="Nominee Directors"
      breadcrumbs={[{ label: 'Nominee Directors' }]}
    >
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Nominee Director Services</h1>
          <p className="text-sm text-muted-foreground">Manage your nominee director appointments</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link href={`${basePath}/nominee_directors/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                New Service
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <SearchInput value={search} placeholder="Search services..." />
        </div>

        {nominee_directors.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Users className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No nominee director services yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first service to get started</p>
            <Button asChild size="sm">
              <Link href={`${basePath}/nominee_directors/new`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Start Your First Service
              </Link>
            </Button>
          </div>
        ) : (
          <div className="border rounded-lg">
            {nominee_directors.map((nd, i) => (
              <Link
                key={nd.id}
                href={`${basePath}/nominee_directors/${nd.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${
                  i > 0 ? 'border-t' : ''
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{nd.business_name}</span>
                      <StageBadge stage={nd.stage} variant="service" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {nd.type_name} · {nd.jurisdiction_label} · {nd.duration_months} months
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <div className="text-xs text-muted-foreground">{formatDate(nd.created_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
